import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import "./AaasDonation.css";
import "react-phone-input-2/lib/style.css";
import PhoneInput from "react-phone-input-2";
import countries from "./countries.json";
import states from "./states.json";  
import Aos from 'aos';
import 'aos/dist/aos.css';
import stripePromise from '../config/stripe-config';
import { Elements, PaymentRequestButtonElement, useStripe } from '@stripe/react-stripe-js';
import SimplePaymentForm from './SimplePaymentForm';
import StripeCheckoutButton from './StripeCheckoutButton';
import SuccessPopup from '../SuccessPopup'

const GOOGLE_PLACES_API_KEY = process.env.REACT_APP_GOOGLE_PLACES_API_KEY;

// 🚨 FORCE DISABLE EXIT POPUP
window.onbeforeunload = null;

const validateEmail = (email) => {
  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  
  if (!emailRegex.test(email)) {
    return { valid: false, message: "Please enter a valid email address" };
  }
  
  const suspiciousDomains = ['10minutemail.com', 'tempmail.org', 'guerrillamail.com', 'mailinator.com'];
  const domain = email.split('@')[1];
  
  if (suspiciousDomains.includes(domain)) {
    return { valid: false, message: "Temporary email addresses are not allowed" };
  }
  
  if (email.match(/^[a-z]@[a-z]+\.[a-z]+$/i)) {
    return { valid: false, message: "Please enter your real email address" };
  }
  
  return { valid: true };
};

const validateName = (name, fieldName) => {
  if (name.length < 2) {
    return { valid: false, message: `${fieldName} must be at least 2 characters` };
  }
  
  if (/[0-9@#$%^&*()_+=\[\]{}|\\:";'<>?,./]/.test(name)) {
    return { valid: false, message: `${fieldName} contains invalid characters` };
  }
  
  if (/^(.)\1+$/.test(name)) {
    return { valid: false, message: `Please enter your real ${fieldName.toLowerCase()}` };
  }
  
  const invalidNames = ['test', 'fake', 'xxx', 'aaa', 'aza', 'abc', 'qwe', 'asdf', 'name'];
  if (invalidNames.includes(name.toLowerCase())) {
    return { valid: false, message: `Please enter your real ${fieldName.toLowerCase()}` };
  }
  
  if (!/[aeiouAEIOU]/.test(name)) {
    return { valid: false, message: `Please enter a valid ${fieldName.toLowerCase()}` };
  }
  
  return { valid: true };
};

const validateAddress = (address) => {
  if (address.length < 3) {
    return { valid: false, message: "Address is too short" };
  }
  
  if (/^(.)\1+$/.test(address)) {
    return { valid: false, message: "Please enter a valid address" };
  }
  
  const invalidAddresses = ['test', 'fake', 'address', 'street', 'road', 'abc', 'aza'];
  if (invalidAddresses.includes(address.toLowerCase().trim())) {
    return { valid: false, message: "Please enter your real address" };
  }
  
  if (address.trim().split(/\s+/).length < 2) {
    return { valid: false, message: "Please enter a complete address" };
  }
  
  return { valid: true };
};

const validateLocation = (city, country, zipCode) => {
  const errors = [];
  
  if (city.length < 2) {
    errors.push("City name is too short");
  }
  
  if (country === 'US' && !/^\d{5}(-\d{4})?$/.test(zipCode)) {
    errors.push("Invalid US ZIP code format");
  }
  
  if (country === 'CA' && !/^[A-Za-z]\d[A-Za-z] ?\d[A-Za-z]\d$/.test(zipCode)) {
    errors.push("Invalid Canadian postal code format");
  }
  
  return {
    valid: errors.length === 0,
    message: errors.join(', ')
  };
};

const Donation = () => {
  const getInitialLang = () => {
    const path = window.location.pathname;
    if (path.startsWith('/en')) return 'en';
    if (path.startsWith('/es')) return 'es';
    const params = new URLSearchParams(window.location.search);
    const langParam = params.get('lang');
    if (langParam === 'en' || langParam === 'es') return langParam;
    return 'fr';
  };
  const [lang, setLang] = useState(getInitialLang);
  const [isMonthly, setIsMonthly] = useState(false);
  const [selectedAmount, setSelectedAmount] = useState(100);
  const [tooltip, setTooltip] = useState(null);
  const [shakeInput, setShakeInput] = useState(false);
  const [activeQuestion, setActiveQuestion] = useState(null); 
  const [hasSeenPopup, setHasSeenPopup] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [popupType, setPopupType] = useState('');
  const [email, setEmail] = useState(""); 
  const [isError, setIsError] = useState(false);
  const [animateLogo, setAnimateLogo] = useState(false);
  const [coverFees, setCoverFees] = useState(false);
  const [isCustomInput, setIsCustomInput] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('checkout');
  const [isGoogleLoaded, setIsGoogleLoaded] = useState(false);
  const [addressAutocomplete, setAddressAutocomplete] = useState(null);
  const [zipAutocomplete, setZipAutocomplete] = useState(null);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successDonationData, setSuccessDonationData] = useState({
    amount: null,
    isMonthly: false
  });

  // 🆕 NOUVEAUX ÉTATS pour Membre/Non-Membre
  const [membershipType, setMembershipType] = useState('non-member'); // 'member' ou 'non-member'
  const [memberNumber, setMemberNumber] = useState('');

  const [step2Data, setStep2Data] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });
  const [errors, setErrors] = useState({});
  const [step3Data, setStep3Data] = useState({
    streetAddress: "",
    addressLine2: "",
    city: "",
    zipCode: "",
    country: "",
    state: "",
  });

  const [donationType, setDonationType] = useState('individual');
  const [donationPurpose, setDonationPurpose] = useState('');
  const [dedication, setDedication] = useState('');
  const [organizationData, setOrganizationData] = useState({
    companyName: '',
    taxId: ''
  });

  const minimumAmount = 20;
  const maximumAmount = 10000;
  const donationOptions = [20, 40, 50, 100, 1000];

  const CHARACTER_LIMITS = {
    firstName: 30,    
    lastName: 30,
    email: 40,       
    streetAddress: 50,
    city: 30,
    zipCode: 20
  };

const translations = {
  fr: {
    heroTitle: "Notre Action De Solidarité Depuis 1890",
    heroSubtitle: "À travers une tradition centenaire, les actions de l'AAAS incarnent un esprit de solidarité et d'entraide. Quelle que soit la forme de votre contribution, une cotisation ou un don, vous rejoignez notre cause et apportez espoir et soutien à ceux qui ont besoin de notre aide.",
    heroHighlight: "Soutenez-nous pour continuer notre Action.",
    
    // 🆕 NOUVEAUX textes pour Membre/Non-Membre
    memberAAAS: "Membre de l'AAAS",
    nonMember: "Non-Membre",
    memberNumber: "Numéro d'adhérent, ex : Z01891 (obligatoire)",
    
    securePaymentTitle: "Paiement Sécurisé",
    securePaymentText: "Le paiement est entièrement sécurisé grâce à Stripe, l'un des leaders mondiaux des solutions de paiement en ligne. Vos informations bancaires sont cryptées et ne sont jamais stockées sur notre site. Stripe est certifié PCI DSS Niveau 1, le plus haut standard de sécurité du secteur, et ses outils avancés de détection de fraude garantissent une transaction fiable et protégée.",
    taxDeductionTitle: "Déduction Fiscale",
    taxDeductionText: "<strong>Particuliers :</strong> En faisant un don à l'AAAS, vous pouvez bénéficier d'une déduction fiscale de <strong style='color: #08337c;'>66 %</strong> sur votre impôt sur le revenu en France, dans la limite de <strong style='color: #08337c;'>20 %</strong> de votre revenu imposable. Ainsi, un don de 50 € ouvre droit à une réduction d'impôt de 33 €, tandis qu'un don de 100 € permet une réduction d'impôt de 66 €, etc.<br/><br/><strong>Entreprises :</strong> Les entreprises peuvent bénéficier d'une réduction d'impôt sur les sociétés au titre des dons effectués dans le cadre du mécénat selon les modalités prévues à l'art. 238 bis du CGI. Le taux de cette réduction est de : <strong style='color: #08337c;'>60 %</strong> du montant des dons (&lt; 2 millions d'euros), dans la limite de <strong style='color: #08337c;'>20 000 €</strong> ou de <strong style='color: #08337c;'>0,5 %</strong> du chiffre d'affaires annuel hors taxes, si ce dernier montant est plus élevé. <strong style='color: #08337c;'>40 %</strong> pour la fraction des dons supérieure à 2 millions d'euros. En cas de dépassement de ces plafonds, l'excédent peut être reporté sur les cinq exercices suivants.",
    taxDeductionReceipt: "Après votre paiement, un e-mail de confirmation vous sera automatiquement envoyé. Le reçu fiscal (Cerfa), qui vous permettra de demander la déduction fiscale, vous sera transmis dès que notre compte aura été crédité.",
    dataProcessingTitle: "Traitement de vos données",
    dataProcessingText: "L'AAAS traite les données collectées auprès de nos donateurs pour gérer votre don et vous tenir informé de nos activités. Cela inclut l'envoi de votre reçu fiscal. Conformément à la réglementation en vigueur, vous disposez de droits concernant ces données. Pour en savoir plus et exercer vos droits, cliquez ici pour consulter notre politique de protection des données. Nous ne partageons pas vos données avec des tiers.",
    other: "Autre",
    oneTime: "Don unique",
    monthly: "Don régulier",
    otherAmount: "Autre montant",
    enterAmount: "Montant libre",
    billingInfo: "Mes coordonnées",
    purposeLabel: "Objet du don (facultatif)",
    purposeGeneral: "Soutien général",
    purposeEducation: "Programmes éducatifs",
    purposeHealthcare: "Assistance médicale",
    purposeEmergency: "Aide d'urgence",
    purposeVocational: "Formation professionnelle",
    purposeOther: "Autre",
    dedicationPlaceholder: "Dédicace (Optionnel)",
    organizationCheckbox: "Je donne au nom d'une organisation ou d'une entreprise",
    companyName: "Nom de l'entreprise *",
    taxId: "SIRET / Numéro fiscal (facultatif)",
    firstName: "Prénom *",
    lastName: "Nom *",
    email: "Adresse e-mail *",
    phone: "Numéro de téléphone (facultatif)",
    address: "Adresse *",
    city: "Ville *",
    state: "État/Province *",
    zipCode: "Code postal *",
    country: "Pays *",
    selectCountryFirst: "Sélectionnez d'abord le pays",
    noStates: "Aucun état/province pour ce pays",
    coverFees: "Je souhaite couvrir les frais associés à mon don afin que la totalité de mon don aille directement à",
    originalDonation: "Don initial :",
    processingFees: "Frais de traitement (5%) :",
    totalAmount: "Montant total :",
    donateButton: "Faire un don de",
    stripeRedirect: "Vous serez redirigé vers la page de paiement sécurisée de Stripe",
    questionSecure: "Mon don est-il sécurisé ?",
    answerSecure: "Le paiement est entièrement sécurisé grâce à Stripe, l'un des leaders mondiaux des solutions de paiement en ligne. Vos informations bancaires sont cryptées et ne sont jamais stockées sur notre site. Stripe est certifié PCI DSS Niveau 1, le plus haut standard de sécurité du secteur, et ses outils avancés de détection de fraude garantissent une transaction fiable et protégée.",
    questionTaxDeductible: "Déduction Fiscale - Contribuables Français",
    answerTaxDeductible: "<strong>Particuliers :</strong> En faisant un don à l'AAAS, vous pouvez bénéficier d'une déduction fiscale de <strong style='color: #08337c;'>66 %</strong> sur votre impôt sur le revenu en France, dans la limite de <strong style='color: #08337c;'>20 %</strong> de votre revenu imposable. Ainsi, un don de 50 € ouvre droit à une réduction d'impôt de 33 €, tandis qu'un don de 100 € permet une réduction d'impôt de 66 €, etc.<br/><br/><strong>Entreprises :</strong> Les entreprises peuvent bénéficier d'une réduction d'impôt sur les sociétés au titre des dons effectués dans le cadre du mécénat selon les modalités prévues à l'art. 238 bis du CGI. Le taux de cette réduction est de : <strong style='color: #08337c;'>60 %</strong> du montant des dons (&lt; 2 millions d'euros), dans la limite de <strong style='color: #08337c;'>20 000 €</strong> ou de <strong style='color: #08337c;'>0,5 %</strong> du chiffre d'affaires annuel hors taxes, si ce dernier montant est plus élevé. <strong style='color: #08337c;'>40 %</strong> pour la fraction des dons supérieure à 2 millions d'euros. En cas de dépassement de ces plafonds, l'excédent peut être reporté sur les cinq exercices suivants.",
    answerTaxReceipt: "<br/>Après votre paiement, un e-mail de confirmation vous sera automatiquement envoyé. <strong>Le reçu fiscal (Cerfa)</strong>, qui vous permettra de demander la déduction fiscale, vous sera transmis dès que notre compte aura été crédité.",
    questionDataProcessing: "Traitement de vos données",
    answerDataProcessing: "L'AAAS traite les données collectées auprès de nos donateurs pour gérer votre don et vous tenir informé de nos activités. Cela inclut l'envoi de votre reçu fiscal. Conformément à la réglementation en vigueur, vous disposez de droits concernant ces données. Pour en savoir plus et exercer vos droits, cliquez ici pour consulter notre politique de protection des données. Nous ne partageons pas vos données avec des tiers.",
    questionCancelRecurring: "Puis-je annuler mon don récurrent ?",
    answerCancelRecurring: "Bien sûr, vous pouvez annuler ou modifier votre don récurrent à tout moment. Pour toute demande concernant vos dons récurrents, comme un changement de montant, de date de paiement, d'un moyen de paiement ou d'annulation, veuillez contacter notre équipe de soutien aux donateurs à l'adresse",
    answerCancelRecurringEmail: "dons@aaas.fr",
    answerCancelRecurringEnd: "Nous vous assisterons dans les plus brefs délais.",
    footerContact: "Pour toute question, contactez-nous :",
    footerCopyright: "© 2026 AAAS. Tous droits réservés.",
    footerPrivacy: "Politique de confidentialité",
    companyInfo: "Association Arménienne d'Aide Sociale",
    thankYou: "Nous vous remercions pour votre générosité."
  },

  en: {
    heroTitle: "Our Solidarity in Action Since 1890",
    heroSubtitle: "Through a century-old tradition, AAAS actions embody a spirit of solidarity and mutual aid. Whatever form your contribution takes, whether a membership fee or a donation, you join our cause, bringing hope and support to those who need our help.",
    heroHighlight: "Support us to continue our Action.",
    
    // 🆕 NOUVEAUX textes pour Membre/Non-Membre
    memberAAAS: "AAAS Member",
    nonMember: "Non-Member",
    memberNumber: "Member number, e.g. Z01891 (required)",
    
    securePaymentTitle: "Secure Payment",
    securePaymentText: "Payment is entirely secured thanks to Stripe, one of the world's leading online payment solutions. Your banking information is encrypted and is never stored on our site. Stripe is PCI DSS Level 1 certified, the highest security standard in the industry, and its advanced fraud detection tools guarantee reliable, secure transactions.",
    taxDeductionTitle: "Tax Deduction",
    taxDeductionText: "<strong>Individuals:</strong> By donating to AAAS, you may benefit from a tax deduction of <strong style='color: #08337c;'>66%</strong> on your income tax in France, up to <strong style='color: #08337c;'>20%</strong> of your taxable income. For example, a donation of €50 grants a tax reduction of €33, while a donation of €100 allows a tax reduction of €66, etc.<br/><br/><strong>Companies:</strong> Companies can benefit from a corporate tax reduction for donations made under patronage according to the provisions of art. 238 bis of the CGI. The rate of this reduction is: <strong style='color: #08337c;'>60%</strong> of the donation amount (&lt; 2 million euros), up to <strong style='color: #08337c;'>€20,000</strong> or <strong style='color: #08337c;'>0.5%</strong> of annual turnover excluding tax, if the latter amount is higher. <strong style='color: #08337c;'>40%</strong> for donations exceeding 2 million euros. In case of exceeding these limits, the excess can be carried forward over the next five fiscal years.",
    taxDeductionReceipt: "After your payment, a confirmation email will be automatically sent to you. The tax receipt (Cerfa), which will allow you to request the tax deduction, will be sent to you as soon as our account has been credited.",
    dataProcessingTitle: "Data Processing",
    dataProcessingText: "AAAS processes data collected from our donors to manage your donation and keep you informed of our activities. This includes sending your tax receipt. In accordance with current regulations, you have rights regarding this data. To learn more and exercise your rights, click here to consult our data protection policy. We do not share your data with third parties.",
    other: "Other",
    oneTime: "One-Time",
    monthly: "Monthly",
    otherAmount: "Other amount",
    enterAmount: "Enter amount",
    billingInfo: "My contact details",
    purposeLabel: "Donation purpose (optional)",
    purposeGeneral: "General support",
    purposeEducation: "Educational programs",
    purposeHealthcare: "Medical assistance",
    purposeEmergency: "Emergency aid",
    purposeVocational: "Vocational training",
    purposeOther: "Other",
    dedicationPlaceholder: "Dedication (Optional)",
    organizationCheckbox: "I'm donating on behalf of an organization or company",
    companyName: "Company name *",
    taxId: "Tax ID / SIRET (optional)",
    firstName: "First name *",
    lastName: "Last name *",
    email: "Email Address *",
    phone: "Phone number (optional)",
    address: "Address Line *",
    city: "City *",
    state: "State/Province *",
    zipCode: "ZIP/Postal Code *",
    country: "Country *",
    selectCountryFirst: "Select Country First",
    noStates: "No States/Provinces for this country",
    coverFees: "I'd like to cover the fees associated with my donation so more of my donation goes directly to",
    originalDonation: "Original donation:",
    processingFees: "Processing fees (5%):",
    totalAmount: "Total amount:",
    donateButton: "Donate",
    stripeRedirect: "You will be redirected to Stripe's secure payment page",
    questionSecure: "Is my donation secure?",
    answerSecure: "Payment is entirely secured thanks to Stripe, one of the world's leading online payment solutions. Your banking information is encrypted and is never stored on our site. Stripe is PCI DSS Level 1 certified, the highest security standard in the industry, and its advanced fraud detection tools guarantee reliable, secure transactions.",
    questionTaxDeductible: "Tax Deduction - For Taxpayers",
    answerTaxDeductible: "<strong>Individuals:</strong> By donating to AAAS, you may benefit from a tax deduction of <strong style='color: #08337c;'>66%</strong> on your income tax in France, up to <strong style='color: #08337c;'>20%</strong> of your taxable income. For example, a donation of €50 grants a tax reduction of €33, while a donation of €100 allows a tax reduction of €66, etc.<br/><br/><strong>Companies:</strong> Companies can benefit from a corporate tax reduction for donations made under patronage according to the provisions of art. 238 bis of the CGI. The rate of this reduction is: <strong style='color: #08337c;'>60%</strong> of the donation amount (&lt; 2 million euros), up to <strong style='color: #08337c;'>€20,000</strong> or <strong style='color: #08337c;'>0.5%</strong> of annual turnover excluding tax, if the latter amount is higher. <strong style='color: #08337c;'>40%</strong> for donations exceeding 2 million euros. In case of exceeding these limits, the excess can be carried forward over the next five fiscal years.",
    answerTaxReceipt: "<br/>After your payment, a confirmation email will be automatically sent to you. <strong>The tax receipt (Cerfa)</strong>, which will allow you to request the tax deduction, will be sent to you as soon as our account has been credited.",
    questionDataProcessing: "Data Processing",
    answerDataProcessing: "AAAS processes data collected from our donors to manage your donation and keep you informed of our activities. This includes sending your tax receipt. In accordance with current regulations, you have rights regarding this data. To learn more and exercise your rights, click here to consult our data protection policy. We do not share your data with third parties.",
    questionCancelRecurring: "Can I cancel my recurring donation?",
    answerCancelRecurring: "Sure, you can cancel or modify your recurring donation at any time. For any requests pertaining to your recurring donations, such as a change in amount or payment date, updating your payment method, or cancellation, please contact our donor support team at",
    answerCancelRecurringEmail: "dons@aaas.fr",
    answerCancelRecurringEnd: "and we'll assist you right away.",
    footerContact: "For questions, contact us:",
    footerCopyright: "© 2026 AAAS. All rights reserved.",
    footerPrivacy: "Privacy Policy",
    companyInfo: "Association Arménienne d'Aide Sociale",
    thankYou: "Thank you for your generosity."
  },
  
  es: {
    heroTitle: "Nuestra Solidaridad en Acción Desde 1890",
    heroSubtitle: "A través de una tradición centenaria, las acciones de AAAS encarnan un espíritu de solidaridad y ayuda mutua. Cualquiera que sea la forma de su contribución, ya sea una cuota de membresía o una donación, usted se une a nuestra causa, brindando esperanza y apoyo a quienes necesitan nuestra ayuda.",
    heroHighlight: "Apóyenos para continuar nuestra Acción.",
    
    // 🆕 NOUVEAUX textes pour Membre/Non-Membre
    memberAAAS: "Miembro de AAAS",
    nonMember: "No Miembro",
    memberNumber: "Número de miembro, ej: Z01891 (obligatorio)",
    
    securePaymentTitle: "Pago Seguro",
    securePaymentText: "El pago está completamente asegurado gracias a Stripe, una de las principales soluciones de pago en línea del mundo. Su información bancaria está encriptada y nunca se almacena en nuestro sitio. Stripe está certificado PCI DSS Nivel 1, el estándar de seguridad más alto de la industria, y sus herramientas avanzadas de detección de fraude garantizan transacciones confiables y seguras.",
    taxDeductionTitle: "Deducción Fiscal",
    taxDeductionText: "<strong>Particulares:</strong> Al hacer una donación a AAAS, puede beneficiarse de una deducción fiscal del <strong style='color: #08337c;'>66%</strong> en su impuesto sobre la renta en Francia, hasta el <strong style='color: #08337c;'>20%</strong> de su renta imponible. Por ejemplo, una donación de 50€ otorga una reducción de impuestos de 33€, mientras que una donación de 100€ permite una reducción de impuestos de 66€, etc.<br/><br/><strong>Empresas:</strong> Las empresas pueden beneficiarse de una reducción del impuesto sobre sociedades por las donaciones realizadas en el marco del mecenazgo según las modalidades previstas en el art. 238 bis del CGI. La tasa de esta reducción es de: <strong style='color: #08337c;'>60%</strong> del monto de las donaciones (&lt; 2 millones de euros), hasta <strong style='color: #08337c;'>20.000€</strong> o <strong style='color: #08337c;'>0,5%</strong> de la facturación anual sin impuestos, si este último monto es mayor. <strong style='color: #08337c;'>40%</strong> para la fracción de donaciones superior a 2 millones de euros. En caso de superar estos límites, el excedente puede trasladarse a los cinco ejercicios siguientes.",
    taxDeductionReceipt: "Después de su pago, se le enviará automáticamente un correo electrónico de confirmación. El recibo fiscal (Cerfa), que le permitirá solicitar la deducción fiscal, se le enviará tan pronto como nuestra cuenta haya sido acreditada.",
    dataProcessingTitle: "Procesamiento de Datos",
    dataProcessingText: "AAAS procesa los datos recopilados de nuestros donantes para gestionar su donación y mantenerle informado de nuestras actividades. Esto incluye el envío de su recibo fiscal. De acuerdo con la normativa vigente, usted tiene derechos sobre estos datos. Para obtener más información y ejercer sus derechos, haga clic aquí para consultar nuestra política de protección de datos. No compartimos sus datos con terceros.",
    other: "Otro",
    oneTime: "Una vez",
    monthly: "Mensual",
    otherAmount: "Otro monto",
    enterAmount: "Ingrese el monto",
    billingInfo: "Mis datos de contacto",
    purposeLabel: "Propósito de la donación (opcional)",
    purposeGeneral: "Apoyo general",
    purposeEducation: "Programas educativos",
    purposeHealthcare: "Asistencia médica",
    purposeEmergency: "Ayuda de emergencia",
    purposeVocational: "Formación profesional",
    purposeOther: "Otro",
    dedicationPlaceholder: "Dedicatoria (Opcional)",
    organizationCheckbox: "Estoy donando en nombre de una organización o empresa",
    companyName: "Nombre de la empresa *",
    taxId: "Número fiscal / SIRET (opcional)",
    firstName: "Nombre *",
    lastName: "Apellido *",
    email: "Dirección de correo electrónico *",
    phone: "Número de teléfono (opcional)",
    address: "Dirección *",
    city: "Ciudad *",
    state: "Estado/Provincia *",
    zipCode: "Código postal *",
    country: "País *",
    selectCountryFirst: "Seleccione el país primero",
    noStates: "No hay estados/provincias para este país",
    coverFees: "Me gustaría cubrir las tarifas asociadas con mi donación para que más de mi donación vaya directamente a",
    originalDonation: "Donación original:",
    processingFees: "Tarifas de procesamiento (5%):",
    totalAmount: "Monto total:",
    donateButton: "Donar",
    stripeRedirect: "Será redirigido a la página de pago segura de Stripe",
    questionSecure: "¿Es segura mi donación?",
    answerSecure: "El pago está completamente asegurado gracias a Stripe, una de las principales soluciones de pago en línea del mundo. Su información bancaria está encriptada y nunca se almacena en nuestro sitio. Stripe está certificado PCI DSS Nivel 1, el estándar de seguridad más alto de la industria, y sus herramientas avanzadas de detección de fraude garantizan transacciones confiables y seguras.",
    questionTaxDeductible: "Deducción Fiscal - Para Contribuyentes",
    answerTaxDeductible: "<strong>Particulares:</strong> Al hacer una donación a AAAS, puede beneficiarse de una deducción fiscal del <strong style='color: #08337c;'>66%</strong> en su impuesto sobre la renta en Francia, hasta el <strong style='color: #08337c;'>20%</strong> de su renta imponible. Por ejemplo, una donación de 50€ otorga una reducción de impuestos de 33€, mientras que una donación de 100€ permite una reducción de impuestos de 66€, etc.<br/><br/><strong>Empresas:</strong> Las empresas pueden beneficiarse de una reducción del impuesto sobre sociedades por las donaciones realizadas en el marco del mecenazgo según las modalidades previstas en el art. 238 bis del CGI. La tasa de esta reducción es de: <strong style='color: #08337c;'>60%</strong> del monto de las donaciones (&lt; 2 millones de euros), hasta <strong style='color: #08337c;'>20.000€</strong> o <strong style='color: #08337c;'>0,5%</strong> de la facturación anual sin impuestos, si este último monto es mayor. <strong style='color: #08337c;'>40%</strong> para la fracción de donaciones superior a 2 millones de euros. En caso de superar estos límites, el excedente puede trasladarse a los cinco ejercicios siguientes.",
    answerTaxReceipt: "<br/>Después de su pago, se le enviará automáticamente un correo electrónico de confirmación. <strong>El recibo fiscal (Cerfa)</strong>, que le permitirá solicitar la deducción fiscal, se le enviará tan pronto como nuestra cuenta haya sido acreditada.",
    questionDataProcessing: "Procesamiento de Datos",
    answerDataProcessing: "AAAS procesa los datos recopilados de nuestros donantes para gestionar su donación y mantenerle informado de nuestras actividades. Esto incluye el envío de su recibo fiscal. De acuerdo con la normativa vigente, usted tiene derechos sobre estos datos. Para obtener más información y ejercer sus derechos, haga clic aquí para consultar nuestra política de protección de datos. No compartimos sus datos con terceros.",
    questionCancelRecurring: "¿Puedo cancelar mi donación recurrente?",
    answerCancelRecurring: "Por supuesto, puede cancelar o modificar su donación recurrente en cualquier momento. Para cualquier solicitud relacionada con sus donaciones recurrentes, como un cambio de monto o fecha de pago, actualización de su método de pago o cancelación, póngase en contacto con nuestro equipo de soporte a donantes en",
    answerCancelRecurringEmail: "dons@aaas.fr",
    answerCancelRecurringEnd: "y le ayudaremos de inmediato.",
    footerContact: "Para preguntas, contáctenos:",
    footerCopyright: "© 2026 AAAS. Todos los derechos reservados.",
    footerPrivacy: "Política de privacidad",
    companyInfo: "Association Arménienne d'Aide Sociale",
    thankYou: "Gracias por su generosidad."
  }
};

const t = translations[lang];

const calculateTotalAmount = () => {
  if (!selectedAmount) return 0;
  
  const baseAmount = parseFloat(selectedAmount) || 0;
  const totalAmount = coverFees ? (baseAmount * 1.05) : baseAmount;
  
  return Math.round(totalAmount * 100) / 100;
};

const handleCoverFeesToggle = () => {
  if (selectedAmount >= minimumAmount) {
    setCoverFees(!coverFees);
  }
};

const isValidAmount = selectedAmount >= minimumAmount && selectedAmount <= 10000;

useEffect(() => {
  window.onbeforeunload = null;
  
  const originalAddEventListener = EventTarget.prototype.addEventListener;
  EventTarget.prototype.addEventListener = function(type, listener, options) {
    if (type === 'beforeunload') {
      console.log('🚫 Blocked beforeunload listener');
      return;
    }
    return originalAddEventListener.call(this, type, listener, options);
  };
  
  const searchParams = new URLSearchParams(window.location.search);
  const paymentStatus = searchParams.get('payment');
  const sessionId = searchParams.get('session_id');
  
  if (paymentStatus === 'success' || sessionId) {
    console.log('✅ Payment completed successfully!');
    
    const savedDonationData = JSON.parse(localStorage.getItem('pendingDonation') || '{}');
    
    setSuccessDonationData({
      amount: savedDonationData.amount || selectedAmount,
      isMonthly: savedDonationData.isMonthly || isMonthly
    });
    
    window.history.replaceState({}, '', window.location.pathname);
    
    setTimeout(() => {
      setShowSuccessPopup(true);
    }, 500);
    
    localStorage.removeItem('pendingDonation');
  }
  
  return () => {
    window.onbeforeunload = null;
  };
}, []);

const handleFieldChange = (field, value, setData) => {
  const limit = CHARACTER_LIMITS[field];
  if (limit && value.length > limit) {
    setErrors(prev => ({
      ...prev,
      [field]: `${field === 'firstName' ? 'First name' : 
                field === 'lastName' ? 'Last name' : 
                field === 'email' ? 'Email' :
                field === 'streetAddress' ? 'Address' :
                field === 'city' ? 'City' : 'Field'} cannot exceed ${limit} characters`
    }));
    return;
  }

  setData(prev => ({ ...prev, [field]: value }));
  
  setErrors(prev => ({
    ...prev,
    [field]: null
  }));
  
  setTimeout(() => {
    let fieldError = null;
    
    if (field === 'firstName' || field === 'lastName') {
      const nameCheck = validateName(value, field === 'firstName' ? 'First name' : 'Last name');
      fieldError = nameCheck.valid ? null : nameCheck.message;
    }
    
    if (field === 'email') {
      const emailCheck = validateEmail(value);
      fieldError = emailCheck.valid ? null : emailCheck.message;
    }
    
    if (field === 'streetAddress') {
      if (value.length > 3) {
        const addressCheck = validateAddress(value);
        fieldError = addressCheck.valid ? null : addressCheck.message;
      }
    }
    
    setErrors(prev => ({
      ...prev,
      [field]: fieldError
    }));
  }, 500);
};

const handleCityChange = (value) => {
  setStep3Data(prev => ({ ...prev, city: value }));
  
  if (value.length > 2) {
    if (window.google?.maps?.Geocoder) {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address: value }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const addressComponents = results[0].address_components;
          let detectedCountry = '';
          let detectedState = '';
          
          addressComponents.forEach(component => {
            if (component.types.includes('country')) {
              detectedCountry = component.short_name;
            }
            if (component.types.includes('administrative_area_level_1')) {
              detectedState = component.short_name;
            }
          });
          
          if (detectedCountry) {
            const countryStates = getStatesForCountry(detectedCountry);
            
            setStep3Data(prev => ({
              ...prev,
              country: detectedCountry,
              state: (countryStates.length > 0 && detectedState) ? detectedState : ""
            }));
            
            setErrors(prev => ({
              ...prev,
              country: null,
              state: countryStates.length > 0 ? prev.state : null
            }));
          }
        }
      });
    }
  }
};

const handleCloseSuccessPopup = () => {
  setShowSuccessPopup(false);
};

const showPopupWithScrollBlock = (type) => {
  setPopupType(type);
  setShowPopup(true);
  setHasSeenPopup(true);
  document.body.style.overflow = 'hidden';
  document.documentElement.style.overflow = 'hidden';
  document.body.style.position = 'fixed';
  document.body.style.width = '100%';
};

const hidePopupAndRestoreScroll = () => {
  setShowPopup(false);
  document.body.style.overflow = '';
  document.documentElement.style.overflow = '';
  document.body.style.position = '';
  document.body.style.width = '';
};

const handleLogoClick = (e, logoType = 'aaas', source = 'header') => {
  window.open('https://aaas.fr', '_blank', 'noopener,noreferrer');
};

const handleFooterLogoClick = (url) => {
  window.open(url, '_blank', 'noopener,noreferrer');
};

const handleLangChange = (newLang) => {
  setLang(newLang);
  const newPath = newLang === 'fr' ? '/' : `/${newLang}`;
  window.history.pushState({}, '', newPath);
};

const getPopupContent = () => {
  const popups = {
    fr: {
      aaas: {
        title: "Vous partez déjà ?",
        message: "Veuillez laisser votre adresse e-mail ci-dessous, et nous vous enverrons un petit rappel pour finaliser votre don plus tard."
      },
      leaving: {
        title: "Attendez ! Ne partez pas encore !",
        message: "Vous êtes sur le point de quitter la page de don. Souhaitez-vous que nous vous envoyions un rappel pour finaliser votre don plus tard ?"
      },
      default: {
        title: "Peut-être la prochaine fois ?",
        message: "Veuillez laisser votre adresse e-mail ci-dessous, et nous vous enverrons un petit rappel plus tard."
      }
    },
    en: {
      aaas: {
        title: "Leaving so soon?",
        message: "Please leave your email address below, and we'll send you a gentle reminder to complete your donation later."
      },
      leaving: {
        title: "Wait! Don't leave yet!",
        message: "You're about to leave the donation page. Would you like us to send you a reminder to complete your donation later?"
      },
      default: {
        title: "Maybe next time?",
        message: "Please leave your email address below, and we'll send you a gentle reminder later."
      }
    },
    es: {
      aaas: {
        title: "¿Se va tan pronto?",
        message: "Por favor, deje su dirección de correo electrónico a continuación, y le enviaremos un recordatorio para completar su donación más tarde."
      },
      leaving: {
        title: "¡Espere! ¡No se vaya todavía!",
        message: "Está a punto de abandonar la página de donación. ¿Le gustaría que le enviáramos un recordatorio para completar su donación más tarde?"
      },
      default: {
        title: "¿Quizás la próxima vez?",
        message: "Por favor, deje su dirección de correo electrónico a continuación, y le enviaremos un recordatorio más tarde."
      }
    }
  };

  const currentLang = popups[lang] || popups.en;
  
  if (popupType === 'aaas') {
    return currentLang.aaas;
  } else if (popupType === 'leaving') {
    return currentLang.leaving;
  }
  return currentLang.default;
};

const handleNoThanks = () => {
  hidePopupAndRestoreScroll();
  if (popupType === 'aaas') {
    window.location.href = 'https://aaas.fr';
  } else if (popupType === 'leaving') {
    window.location.href = 'https://aaas.fr';
  }
};

const handleRemindMe = async () => {
  console.log('🚀 handleRemindMe called with email:', email);
  
  if (email.includes("@")) {
    console.log('🚀 Sending email to backend:', email);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/collect-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
          source: 'exit_intent',
          timestamp: new Date().toISOString(),
          page: window.location.pathname,
          referrer: document.referrer,
          userAgent: navigator.userAgent
        }),
      });
      
      console.log('📨 Response status:', response.status);
      const result = await response.json();
      console.log('✅ Backend response:', result);
      
      hidePopupAndRestoreScroll();
      
      if (popupType === 'aaas') {
        window.location.href = 'https://aaas.fr';
      } else if (popupType === 'leaving') {
        window.location.href = 'https://aaas.fr';
      }
      
    } catch (error) {
      console.error('❌ Error sending email to backend:', error);
      hidePopupAndRestoreScroll();
    }
  } else {
    setIsError(true);
    setTimeout(() => setIsError(false), 500);
  }
};

const handleMonthlyClick = () => {
  setIsMonthly(true); 
  setAnimateLogo(true); 
  setTimeout(() => setAnimateLogo(false), 300); 
};

const handleClickOutside = (e) => {
  if (!e.target.closest(".question-wrapper")) {
    setActiveQuestion(null);
  }
};

const closeTooltip = () => {
  setTooltip(null); 
};

const toggleQuestion = (question) => {
  setActiveQuestion((prev) => (prev === question ? null : question));
};

const getStatesForCountry = (countryCode) => {
  return states.filter(state => state.country_code === countryCode);
};

const validateAllFields = () => {
  const newErrors = {};
  
  // 🆕 Validation pour les membres AAAS avec traductions
  if (membershipType === 'member') {
    if (!memberNumber.trim()) {
      newErrors.memberNumber = lang === 'fr' ? "Le numéro d'adhérent est obligatoire" : 
                                lang === 'es' ? "El número de miembro es obligatorio" :
                                "Member number is required";
    } else if (memberNumber.trim().length < 3) {
      newErrors.memberNumber = lang === 'fr' ? "Numéro d'adhérent invalide" :
                                lang === 'es' ? "Número de miembro inválido" :
                                "Invalid member number";
    }
    // VALIDATION DU FORMAT himna : Z + 5 chiffres (ex : Z01891)
    else if (!/^Z[0-9]{5}$/i.test(memberNumber.trim())) {
      newErrors.memberNumber = lang === 'fr' ? "Format invalide : Z + 5 chiffres (ex : Z01891)" :
                                lang === 'es' ? "Formato inválido: Z + 5 dígitos (ej: Z01891)" :
                                "Invalid format: Z + 5 digits (e.g., Z01891)";
    }
  }
  
  if (donationType === 'individual') {
    const firstNameCheck = validateName(step2Data.firstName, 
      lang === 'fr' ? 'Prénom' : lang === 'es' ? 'Nombre' : 'First name'
    );
    if (!firstNameCheck.valid) {
      newErrors.firstName = firstNameCheck.message;
    }
    
    const lastNameCheck = validateName(step2Data.lastName, 
      lang === 'fr' ? 'Nom' : lang === 'es' ? 'Apellido' : 'Last name'
    );
    if (!lastNameCheck.valid) {
      newErrors.lastName = lastNameCheck.message;
    }
  }
  
  if (donationType === 'organization') {
    if (!organizationData.companyName.trim()) {
      newErrors.companyName = lang === 'fr' ? "Le nom de l'entreprise est obligatoire" :
                              lang === 'es' ? "El nombre de la empresa es obligatorio" :
                              "Company name is required";
    }
  }
  
  const emailCheck = validateEmail(step2Data.email);
  if (!emailCheck.valid) {
    newErrors.email = emailCheck.message;
  }
  
  if (!step3Data.streetAddress.trim()) {
    newErrors.streetAddress = lang === 'fr' ? "L'adresse est obligatoire" :
                               lang === 'es' ? "La dirección es obligatoria" :
                               "Address is required";
  } else {
    const addressCheck = validateAddress(step3Data.streetAddress);
    if (!addressCheck.valid) {
      newErrors.streetAddress = addressCheck.message;
    }
  }
  
  if (!step3Data.city.trim()) {
    newErrors.city = lang === 'fr' ? "La ville est obligatoire" :
                     lang === 'es' ? "La ciudad es obligatoria" :
                     "City is required";
  } else if (step3Data.city.length < 2) {
    newErrors.city = lang === 'fr' ? "Le nom de la ville est trop court" :
                     lang === 'es' ? "El nombre de la ciudad es demasiado corto" :
                     "City name is too short";
  }
  
  if (!step3Data.zipCode.trim()) {
    newErrors.zipCode = lang === 'fr' ? "Le code postal est obligatoire" :
                        lang === 'es' ? "El código postal es obligatorio" :
                        "ZIP/Postal code is required";
  }
  
  if (!step3Data.country.trim()) {
    newErrors.country = lang === 'fr' ? "Le pays est obligatoire" :
                        lang === 'es' ? "El país es obligatorio" :
                        "Country is required";
  }
  
  if (step3Data.country) {
    const countryStates = getStatesForCountry(step3Data.country);
    if (countryStates.length > 0 && !step3Data.state.trim()) {
      newErrors.state = lang === 'fr' ? "L'état/province est obligatoire pour ce pays" :
                        lang === 'es' ? "El estado/provincia es obligatorio para este país" :
                        "State/Province is required for this country";
    }
  }
  
  if (step3Data.city && step3Data.country && step3Data.zipCode) {
    const locationCheck = validateLocation(step3Data.city, step3Data.country, step3Data.zipCode);
    if (!locationCheck.valid) {
      newErrors.location = locationCheck.message;
    }
  }
  
  return newErrors;
};

const handlePaymentSubmit = () => {
  if (!selectedAmount || selectedAmount < minimumAmount || selectedAmount > maximumAmount) {
    let errorMessage;
    if (!selectedAmount || selectedAmount < minimumAmount) {
      if (lang === 'es') {
        errorMessage = '¡Apreciamos su generosidad! El monto mínimo de donación es de 20 €';
      } else if (lang === 'en') {
        errorMessage = 'We appreciate your generosity! Our minimum donation amount is 20 €';
      } else {
        errorMessage = 'Nous apprécions votre générosité ! Le montant minimum de don est de 20 €';
      }
    } else if (selectedAmount > maximumAmount) {
      errorMessage = "Please enter a donation amount between 20 € and 10,000 €. For donations beyond the maximum amount, please contact us directly. Thank you.";
    }
    
    setErrors(prev => ({
      ...prev,
      amount: errorMessage
    }));
    setShakeInput(true);
    setTimeout(() => setShakeInput(false), 500);
    
    const customInput = document.querySelector('.custom-amount-input');
    if (customInput) {
      customInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    
    return false;
  }

  const validationErrors = validateAllFields();
  
  if (Object.keys(validationErrors).length > 0) {
    setErrors(prev => ({ ...prev, ...validationErrors }));
    setShakeInput(true);
    setTimeout(() => setShakeInput(false), 500);
    
    const firstErrorField = document.querySelector('.form-input.error');
    if (firstErrorField) {
      firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    
    const errorMessages = Object.values(validationErrors);
    alert(`Please correct the following issues:\n\n${errorMessages.join('\n')}`);
    return false;
  }
  
  // 🆕 SAUVEGARDE avec membershipType et memberNumber
  localStorage.setItem('pendingDonation', JSON.stringify({
    amount: calculateTotalAmount(),
    isMonthly: isMonthly,
    donationType: donationType,
    donationPurpose: donationPurpose,
    dedication: dedication,
    organizationData: organizationData,
    membershipType: membershipType,
    memberNumber: memberNumber,
    timestamp: Date.now()
  }));
  
  return true;
};

useEffect(() => {
  const setupFixedBackground = () => {
    console.log('Setting up fixed Ararat background...');
    
    document.body.style.margin = '0';
    document.body.style.padding = '0';
    document.body.style.overflow = 'auto';
    document.documentElement.style.margin = '0';
    document.documentElement.style.padding = '0';
    
    console.log('Fixed Ararat background configured');
  };

  const setupMobileBackground = () => {
    if (window.innerWidth <= 1024) {
      document.body.style.overflow = 'auto';
      document.documentElement.style.overflow = 'auto';
      console.log('Mobile/tablet mode activated');
    }
  };

  setupFixedBackground();
  setupMobileBackground();
  
  const handleResize = () => {
    setupMobileBackground();
  };
  
  window.addEventListener('resize', handleResize);
  
  return () => {
    window.removeEventListener('resize', handleResize);
  };
}, []);

useEffect(() => {
  if (!GOOGLE_PLACES_API_KEY) return;

  let mapsLoaded = false;
  
  const loadGoogleMapsOnDemand = () => {
    if (mapsLoaded) return;
    
    console.log('🗺️ Chargement Google Maps à la demande (économie 163KB)...');
    
    if (window.google?.maps?.places) {
      console.log('✅ Google Maps déjà disponible');
      setIsGoogleLoaded(true);
      mapsLoaded = true;
      return;
    }

    if (document.querySelector(`script[src*="maps.googleapis.com"]`)) {
      console.log('📜 Script Google Maps existe, attente du chargement...');
      const checkLoaded = () => {
        if (window.google?.maps?.places) {
          console.log('✅ Google Maps maintenant prêt!');
          setIsGoogleLoaded(true);
          mapsLoaded = true;
        } else {
          setTimeout(checkLoaded, 100);
        }
      };
      checkLoaded();
      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_PLACES_API_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;
    
    script.onload = () => {
      console.log('✅ Google Maps chargé avec succès!');
      setIsGoogleLoaded(true);
      mapsLoaded = true;
    };
    
    script.onerror = (error) => {
      console.error('❌ Erreur chargement Google Maps:', error);
    };
    
    document.head.appendChild(script);
  };

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting && entry.target.id === 'address-input') {
        console.log('👁️ Input adresse visible - chargement Google Maps');
        loadGoogleMapsOnDemand();
        observer.disconnect();
      }
    });
  }, { 
    threshold: 0.1,
    rootMargin: '50px'
  });

  const timer = setTimeout(() => {
    const addressInput = document.getElementById('address-input');
    if (addressInput) {
      observer.observe(addressInput);
      addressInput.addEventListener('focus', loadGoogleMapsOnDemand, { once: true });
      console.log('👀 Observer Google Maps configuré sur #address-input');
    } else {
      console.log('⚠️ Input adresse non trouvé, retry dans 2s...');
      setTimeout(() => {
        const retryInput = document.getElementById('address-input');
        if (retryInput) {
          observer.observe(retryInput);
          retryInput.addEventListener('focus', loadGoogleMapsOnDemand, { once: true });
        }
      }, 2000);
    }
  }, 1000);

  return () => {
    clearTimeout(timer);
    observer.disconnect();
  };
}, []);

useEffect(() => {
  if (!isGoogleLoaded) return;

  const initializeAutocomplete = () => {
    try {
      console.log('Initializing Google Places autocomplete...');
      
      const addressInput = document.getElementById('address-input');
      if (addressInput && window.google?.maps?.places) {
        console.log('Setting up address autocomplete');
        
        const autocomplete = new window.google.maps.places.Autocomplete(addressInput, {
          types: ['address'],
          fields: ['address_components', 'formatted_address', 'geometry']
        });

        autocomplete.addListener('place_changed', () => {
          const place = autocomplete.getPlace();
          console.log('Place selected:', place);
          
          if (!place.address_components) {
            console.log('No address details available');
            return;
          }

          let streetNumber = '';
          let route = '';
          let city = '';
          let state = '';
          let country = '';
          let zipCode = '';

          place.address_components.forEach(component => {
            const componentType = component.types[0];
            
            switch (componentType) {
              case 'street_number':
                streetNumber = component.long_name;
                break;
              case 'route':
                route = component.long_name;
                break;
              case 'locality':
              case 'administrative_area_level_2':
                city = component.long_name;
                break;
              case 'administrative_area_level_1':
                state = component.short_name;
                break;
              case 'country':
                country = component.short_name;
                break;
              case 'postal_code':
                zipCode = component.long_name;
                break;
            }
          });

          const fullAddress = `${streetNumber} ${route}`.trim();
          console.log('Auto-filling address fields:', { fullAddress, city, state, country, zipCode });
          
          setStep3Data(prevData => ({
            ...prevData,
            streetAddress: fullAddress || place.formatted_address || place.name,
            city: city,
            state: state,
            country: country,
            zipCode: zipCode
          }));

          setErrors(prev => ({
            ...prev,
            streetAddress: null,
            city: null,
            state: null,
            country: null,
            zipCode: null,
            location: null
          }));
        });

        setAddressAutocomplete(autocomplete);
        console.log('Address autocomplete initialized successfully');
      }

      const zipInput = document.getElementById('zip-input');
      if (zipInput && window.google?.maps?.places) {
        console.log('Setting up ZIP code autocomplete');
        
        const zipAutocomplete = new window.google.maps.places.Autocomplete(zipInput, {
          types: ['(regions)'],
          fields: ['address_components']
        });

        zipAutocomplete.addListener('place_changed', () => {
          const place = zipAutocomplete.getPlace();
          
          if (place.address_components) {
            place.address_components.forEach(component => {
              if (component.types[0] === 'postal_code') {
                setStep3Data(prev => ({
                  ...prev,
                  zipCode: component.long_name
                }));
              }
            });
          }
        });

        setZipAutocomplete(zipAutocomplete);
        console.log('ZIP autocomplete initialized successfully');
      }

    } catch (error) {
      console.error('Erreur lors de l\'initialisation de l\'autocomplétion:', error);
    }
  };

  const timer = setTimeout(initializeAutocomplete, 500);
  
  return () => {
    clearTimeout(timer);
    if (addressAutocomplete) {
      window.google?.maps?.event?.clearInstanceListeners(addressAutocomplete);
    }
    if (zipAutocomplete) {
      window.google?.maps?.event?.clearInstanceListeners(zipAutocomplete);
    }
  };
}, [isGoogleLoaded]);

useEffect(() => {
  const isMobile = window.innerWidth <= 768;
  
  if (isMobile) {
    console.log('🚀 Mobile détecté - AOS désactivé pour performance');
    const careForm = document.querySelector('.care-style-form');
    if (careForm) {
      careForm.style.opacity = '1';
      careForm.style.transform = 'none';
      careForm.style.visibility = 'visible';
    }
  } else {
    Aos.init({ duration: 1000, once: true });
    console.log('💻 Desktop détecté - AOS activé');
  }
  
  document.addEventListener("click", handleClickOutside);
  
  return () => {
    document.removeEventListener("click", handleClickOutside);
  };
}, []);

return (
  <Elements stripe={stripePromise}>
<Helmet>
  {/* Balises META de base */}
  <title>Faire un don à l'AAAS - Soutenez nos actions depuis 1890</title>
  <meta name="description" content="Soutenez l'AAAS et aidez les Arméniens en situation de précarité. Don ponctuel ou mensuel, sécurisé par Stripe. Reçu fiscal 66% de réduction d'impôt." />
  
  {/* Open Graph pour réseaux sociaux */}
  <meta property="og:title" content="Faire un don à l'AAAS - Association Arménienne d'Aide Sociale" />
  <meta property="og:description" content="Depuis 1890, l'AAAS accompagne les Arméniens en situation de précarité. Faites un don sécurisé et recevez votre reçu fiscal." />
  <meta property="og:image" content="https://donation.avdevex.com/Images/logo_blue-AAAS.png" />
  <meta property="og:url" content="https://donation.avdevex.com" />
  <meta property="og:type" content="website" />
  
  {/* Twitter Card */}
  <meta name="twitter:card" content="summary_large_image" />
  <meta name="twitter:title" content="Faire un don à l'AAAS" />
  <meta name="twitter:description" content="Soutenez l'AAAS depuis 1890. Don sécurisé, reçu fiscal 66%." />
  <meta name="twitter:image" content="https://donation.avdevex.com/Images/logo_blue-AAAS.png" />
  
  {/* Canonical URL */}
  <link rel="canonical" href="https://donation.avdevex.com" />
  
  {/* Langues alternatives */}
  <link rel="alternate" hreflang="fr" href="https://donation.avdevex.com?lang=fr" />
  <link rel="alternate" hreflang="en" href="https://donation.avdevex.com?lang=en" />
  <link rel="alternate" hreflang="es" href="https://donation.avdevex.com?lang=es" />
  <link rel="alternate" hreflang="x-default" href="https://donation.avdevex.com" />
  
  {/* JSON-LD Schema.org */}
  <script type="application/ld+json">
    {`
      {
        "@context": "https://schema.org",
        "@type": "NGO",
        "name": "Association Arménienne d'Aide Sociale",
        "alternateName": "AAAS",
        "url": "https://www.aaas.fr",
        "logo": "https://donation.avdevex.com/Images/logo_blue-AAAS.png",
        "description": "Depuis 1890, l'AAAS accompagne les Arméniens en situation de précarité en France et dans le monde.",
        "foundingDate": "1890",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "77, rue la Fayette",
          "addressLocality": "Paris",
          "postalCode": "75009",
          "addressCountry": "FR"
        },
        "contactPoint": {
          "@type": "ContactPoint",
          "telephone": "+33-1-48-78-02-99",
          "contactType": "Donor Support",
          "email": "dons@aaas.fr",
          "availableLanguage": ["French", "English", "Spanish"]
        },
        "sameAs": [
          "https://www.facebook.com/aaas.france",
          "https://www.instagram.com/aaas_france"
        ]
      }
    `}
  </script>
</Helmet>

    <div className="donation-page" onClick={closeTooltip}>
      
      <div className="donation-visual-section">
      </div>
      
      <div className="donation-container">

        <header className="header" style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
          padding: '20px 40px'
        }}>
      
          <a href="https://aaas.fr"
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => handleLogoClick(e, 'aaas', 'header')}
          >
            <img
              src={require("../Images/logo_blue-AAAS.png")}
              alt="AAAS Logo"
              className="aaas-logo"
              loading="eager" 
              decoding="async"
              width="180" 
              height="40"
              style={{ contentVisibility: 'visible' }} 
            />
          </a>
          
          <div className="language-selector" style={{
            display: 'flex',
            gap: '10px',
            alignItems: 'center'
          }}>
            <button 
              onClick={() => handleLangChange('fr')}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: lang === 'fr' ? '2px solid #08337c' : '2px solid #ddd',
                backgroundColor: lang === 'fr' ? '#08337c' : 'white',
                color: lang === 'fr' ? 'white' : '#08337c',
                fontWeight: lang === 'fr' ? '600' : '400',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
            >
              FR
            </button>
            <button 
              onClick={() => handleLangChange('en')}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: lang === 'en' ? '2px solid #08337c' : '2px solid #ddd',
                backgroundColor: lang === 'en' ? '#08337c' : 'white',
                color: lang === 'en' ? 'white' : '#08337c',
                fontWeight: lang === 'en' ? '600' : '400',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
            >
              EN
            </button>
            <button 
              onClick={() => handleLangChange('es')}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: lang === 'es' ? '2px solid #08337c' : '2px solid #ddd',
                backgroundColor: lang === 'es' ? '#08337c' : 'white',
                color: lang === 'es' ? 'white' : '#08337c',
                fontWeight: lang === 'es' ? '600' : '400',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
            >
              ES
            </button>
          </div>
        </header>

        {/* POPUP DE RAPPEL DÉSACTIVÉ TEMPORAIREMENT */}

        <div className="donation-info">
          <picture>
            <source 
              media="(max-width: 768px)" 
              srcSet={require("../Images/cascade.webp")}
              width="600"
              height="450"
            />
            <img
              src={require("../Images/cascade.webp")}
              alt="AAAS Donation"
              className="donation-image"
              loading="lazy"
              decoding="async"
              style={{ contentVisibility: 'auto', maxWidth: '600px' }}
              width="600"
              height="450"
            />
          </picture>
          <h2 className="donation-info-title">{t.heroTitle}</h2>
          <p className="donation-subtitle">
            {t.heroSubtitle}
          </p>
          <p className="donation-highlight">
            {t.heroHighlight}
          </p>
        </div>

        <div className="donation-form">
          <div className="care-style-form" data-aos="fade-up" data-aos-duration="1000">

            {/* 🆕 SECTION MEMBRE / NON-MEMBRE */}
            <div className="membership-selection" style={{ marginBottom: '20px' }}>
              <div className="membership-buttons" style={{ 
                display: 'flex', 
                gap: '15px', 
                justifyContent: 'center',
                marginBottom: '10px'
              }}>
              <button
                className={`membership-btn ${membershipType === 'member' ? 'active-member' : ''}`}
                onClick={() => {
                  setMembershipType('member');
                  setDedication('');
                }}
                style={{
                  padding: '12px 30px',
                  borderRadius: '8px',
                  border: membershipType === 'member' ? '2px solid #08337c' : '2px solid #ddd',
                  backgroundColor: membershipType === 'member' ? '#08337c' : 'white',
                  color: membershipType === 'member' ? 'white' : '#333',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  fontSize: '16px'
                }}
              >
                {t.memberAAAS}
              </button>
              <button
                className={`membership-btn ${membershipType === 'non-member' ? 'active-non-member' : ''}`}
                onClick={() => {
                  setMembershipType('non-member');
                  setMemberNumber('');
                }}
                style={{
                  padding: '12px 30px',
                  borderRadius: '8px',
                  border: membershipType === 'non-member' ? '2px solid #08337c' : '2px solid #ddd',
                  backgroundColor: membershipType === 'non-member' ? '#08337c' : 'white',
                  color: membershipType === 'non-member' ? 'white' : '#333',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  fontSize: '16px'
                }}
              >
                {t.nonMember}
              </button>
              </div>
            </div>

        <div className="donation-frequency-tabs">
          <button
            className={`frequency-tab ${!isMonthly ? "active" : ""}`}
            onClick={() => setIsMonthly(false)}
          >
            {t.oneTime}
          </button>
          <button
            className={`frequency-tab ${isMonthly ? "active" : ""}`}
            onClick={handleMonthlyClick}
          >
            {t.monthly}
          </button>
        </div>

            <div className="amount-selection-grid">
              {donationOptions.map((amount) => (
                <button
                  key={amount}
                  className={`amount-button ${selectedAmount === amount && !isCustomInput ? "selected" : ""}`}
                  onClick={() => {
                    console.log('Amount clicked:', amount);
                    setSelectedAmount(amount);
                    setIsCustomInput(false);
                    setErrors(prev => ({ ...prev, amount: null }));
                  }}
                >
                  {amount} €
                </button>
              ))}
              
              <button
                className={`amount-button ${isCustomInput ? "selected" : ""}`}
                onClick={() => {
                  setSelectedAmount(0);
                  setIsCustomInput(true);
                  setErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors.amount;
                    return newErrors;
                  });
                  setTimeout(() => {
                    const customInput = document.querySelector('.custom-amount-input');
                    if (customInput) customInput.focus();
                  }, 100);
                }}
              >
                {t.otherAmount}
              </button>
              <div className="custom-amount-input-wrapper">
                <span className="euro-sign">€</span>
                <input
                  type="text"
                  className={`custom-amount-input ${
                    (!isValidAmount && shakeInput) || errors.amount ? "invalid" : ""
                  }`}
                  placeholder=""
                  style={{ textAlign: 'center', paddingLeft: '30px' }}
                  value={isCustomInput 
                    ? (selectedAmount === 0 ? '' : selectedAmount) 
                    : (selectedAmount || '')}
                  inputMode="decimal"
                  pattern="[0-9]*\.?[0-9]*"
                  onFocus={() => {
                    if (!isCustomInput) {
                      setIsCustomInput(true);
                    }
                  }}
                  onChange={(e) => {
                    setIsCustomInput(true);
                    
                    let inputValue = e.target.value;
                    inputValue = inputValue.replace(/[^\d.]/g, "");
                    
                    const decimalParts = inputValue.split('.');
                    if (decimalParts.length > 2) {
                      inputValue = decimalParts[0] + '.' + decimalParts.slice(1).join('');
                    }
                    
                    if (decimalParts[1] && decimalParts[1].length > 2) {
                      inputValue = decimalParts[0] + '.' + decimalParts[1].substring(0, 2);
                    }
                    
                    if (decimalParts[0] && decimalParts[0].length > 5) {
                      inputValue = decimalParts[0].substring(0, 5) + (decimalParts[1] ? '.' + decimalParts[1] : '');
                    }
                    
                    const numericAmount = inputValue === '' ? 0 : parseFloat(inputValue);
                    
                    if (numericAmount > 10000) {
                      return;
                    }
                    
                    setSelectedAmount(numericAmount);
                    
                    setErrors(prev => {
                      const newErrors = { ...prev };
                      delete newErrors.amount;
                      return newErrors;
                    });
                    
                    if (numericAmount > 0 && numericAmount < minimumAmount) {
                      const minMsg = lang === 'es' 
                        ? '¡Apreciamos su generosidad! El monto mínimo de donación es de 20 €'
                        : lang === 'en'
                        ? 'We appreciate your generosity! Our minimum donation amount is 20 €'
                        : 'Nous apprécions votre générosité ! Le montant minimum de don est de 20 €';
                      setErrors(prev => ({
                        ...prev,
                        amount: minMsg
                      }));
                    }
                  }}
                />
                {errors.amount && (
                  <div className="field-error">{errors.amount}</div>
                )}
              </div>
            </div>

          {membershipType === 'member' ? (
            <div className="input-row" style={{ marginTop: '20px', marginBottom: '20px' }}>
              <div className="input-wrapper">
              <input
                type="text"
                className={`form-input ${errors.memberNumber ? "error" : ""}`}
                placeholder={t.memberNumber}
                value={memberNumber}
                maxLength={6}
                pattern="[Zz][0-9]{5}"
                onChange={(e) => {
                  let value = e.target.value.toUpperCase();
                  
                  // BLOQUER : 6 caractères maximum (Z + 5 chiffres)
                  if (value.length > 6) return;
                  
                  // BLOQUER : la lettre Z au début, puis que des chiffres
                  if (value.length === 1 && !/^Z$/.test(value)) {
                    // Si 1er caractère n'est pas une lettre, ne rien faire
                    return;
                  }
                  
                  if (value.length > 1) {
                    // Si 2e caractère et après ne sont pas des chiffres, ne rien faire
                    const firstChar = value[0];
                    const restChars = value.slice(1);
                    
                    if (firstChar !== 'Z' || !/^[0-9]*$/.test(restChars)) {
                      return;
                    }
                  }
                  
                  setMemberNumber(value);
                  if (value.trim()) {
                    setErrors(prev => ({ ...prev, memberNumber: null }));
                  }
                }}
                required
              />
                {errors.memberNumber && (
                  <div className="field-error">
                    {errors.memberNumber}
                  </div>
                )}
              </div>
            </div>
            ) : (
              <div className="input-row" style={{ marginTop: '20px', marginBottom: '20px' }}>
                <input
                  type="text"
                  className="form-input"
                  placeholder={t.dedicationPlaceholder}
                  value={dedication}
                  onChange={(e) => setDedication(e.target.value)}
                  maxLength={100}
                />
              </div>
            )}

            <div className="billing-section">
              <h3 className="section-title">{t.billingInfo}</h3>
              <div className="input-row">
                <label className="cover-fees-checkbox">
                  <input
                    type="checkbox"
                    checked={donationType === 'organization'}
                    onChange={(e) => setDonationType(e.target.checked ? 'organization' : 'individual')}
                  />
                  <span className="checkmark"></span>
                  <span className="cover-fees-text">
                    {t.organizationCheckbox}
                  </span>
                </label>
              </div>

              {donationType === 'organization' && (
                <>
                  <div className="input-row">
                    <div className="input-wrapper">
                      <input
                        type="text"
                        className={`form-input ${errors.companyName ? "error" : ""}`}
                        placeholder={t.companyName}
                        value={organizationData.companyName}
                        onChange={(e) => setOrganizationData(prev => ({...prev, companyName: e.target.value}))}
                      />
                      {errors.companyName && (
                        <div className="field-error">{errors.companyName}</div>
                      )}
                    </div>
                  </div>
                  <div className="input-row">
                    <input
                      type="text"
                      className="form-input"
                      placeholder={t.taxId}
                      value={organizationData.taxId}
                      onChange={(e) => setOrganizationData(prev => ({...prev, taxId: e.target.value}))}
                    />
                  </div>
                </>
              )}
              
              {donationType === 'individual' && (
                <div className="input-row two-columns">
                  <div className="input-wrapper">
                    <input
                      type="text"
                      className={`form-input ${errors.firstName ? "error" : ""}`}
                      placeholder={t.firstName}
                      value={step2Data.firstName}
                      maxLength={50}
                      onChange={(e) => handleFieldChange('firstName', e.target.value, setStep2Data)}
                    />
                    {errors.firstName && (
                      <div className="field-error">{errors.firstName}</div>
                    )}
                  </div>
                  <div className="input-wrapper">
                    <input
                      type="text"
                      className={`form-input ${errors.lastName ? "error" : ""}`}
                      placeholder={t.lastName}
                      value={step2Data.lastName}
                      maxLength={50}
                      onChange={(e) => handleFieldChange('lastName', e.target.value, setStep2Data)}
                    />
                    {errors.lastName && (
                      <div className="field-error">{errors.lastName}</div>
                    )}
                  </div>
                </div>
              )}

              <div className="input-row">
                <div className="input-wrapper">
                  <input
                    type="email"
                    className={`form-input ${errors.email ? "error" : ""}`}
                    placeholder={t.email}
                    value={step2Data.email}
                    maxLength={100}
                    onChange={(e) => handleFieldChange('email', e.target.value, setStep2Data)}
                  />
                  {errors.email && (
                    <div className="field-error">{errors.email}</div>
                  )}
                </div>
              </div>
            <div className="input-row">
              <PhoneInput
                country={"fr"}
                value={step2Data.phone}
                onChange={(value) => setStep2Data(prev => ({ ...prev, phone: value }))}
                containerClass="phone-input-container"
                inputClass="form-input"
                buttonClass="flag-dropdown"
                placeholder={t.phone}
                inputProps={{
                  autoComplete: 'off',
                  name: 'phone-no-autofill'
                }}
              />
            </div>
              <div className="input-row">
                <div className="input-wrapper">
                  <input
                    id="address-input"
                    type="text"
                    className={`form-input ${errors.streetAddress ? "error" : ""}`}
                    placeholder={t.address}
                    value={step3Data.streetAddress}
                    maxLength={100}
                    onChange={(e) => handleFieldChange('streetAddress', e.target.value, setStep3Data)}
                  />
                  {errors.streetAddress && (
                    <div className="field-error">{errors.streetAddress}</div>
                  )}
                </div>
              </div>
              
              <div className="input-row two-columns">
                <div className="input-wrapper">
                  <input
                    type="text"
                    className={`form-input ${errors.city ? "error" : ""}`}
                    placeholder={t.city}
                    value={step3Data.city}
                    onChange={(e) => handleCityChange(e.target.value)}
                  />
                  {errors.city && (
                    <div className="field-error">{errors.city}</div>
                  )}
                </div>
                <div className="input-wrapper">
                  <select
                    className={`form-select ${errors.state ? "error" : ""}`}
                    value={step3Data.state}
                    onChange={(e) => {
                      setStep3Data(prev => ({ ...prev, state: e.target.value }));
                      setErrors(prev => ({ ...prev, state: null }));
                    }}
                    disabled={!step3Data.country}
                  >
                    <option value="">
                      {!step3Data.country 
                        ? t.selectCountryFirst
                        : getStatesForCountry(step3Data.country).length === 0
                          ? t.noStates
                          : step3Data.state 
                            ? `Selected: ${getStatesForCountry(step3Data.country).find(s => s.state_code === step3Data.state)?.name || step3Data.state}`
                            : t.state
                      }
                    </option>
                    {step3Data.country && getStatesForCountry(step3Data.country).map((state) => (
                      <option key={state.id} value={state.state_code}>
                        {state.name}
                      </option>
                    ))}
                  </select>
                  {errors.state && (
                    <div className="field-error">{errors.state}</div>
                  )}
                </div>
              </div>
              
              <div className="input-row two-columns">
                <div className="input-wrapper">
                  <input
                    id="zip-input"
                    type="text"
                    className={`form-input ${errors.zipCode ? "error" : ""}`}
                    placeholder={t.zipCode}
                    value={step3Data.zipCode}
                    onChange={(e) => setStep3Data(prev => ({ ...prev, zipCode: e.target.value }))}
                  />
                  {errors.zipCode && (
                    <div className="field-error">{errors.zipCode}</div>
                  )}
                </div>
                <div className="input-wrapper">
                  <select 
                    className={`form-select ${errors.country ? "error" : ""}`}
                    value={step3Data.country}
                    onChange={(e) => {
                      setStep3Data(prev => ({ 
                        ...prev, 
                        country: e.target.value,
                        state: ""
                      }));
                      setErrors(prev => ({ ...prev, country: null }));
                    }}
                  >
                    <option value="">
                      {step3Data.country ? `Selected: ${countries.find(c => c["alpha-2"] === step3Data.country)?.name || step3Data.country}` : t.country}
                    </option>
                    {countries.map((country) => (
                      <option key={country["alpha-2"]} value={country["alpha-2"]}>
                        {country.name}
                      </option>
                    ))}
                  </select>
                  {errors.country && (
                    <div className="field-error">{errors.country}</div>
                  )}
                </div>
              </div>
              
              {errors.location && (
                <div className="field-error global-error">{errors.location}</div>
              )}
            </div>

            <div className="cover-fees-section">
              <label className="cover-fees-checkbox">
                <input
                  type="checkbox"
                  checked={coverFees}
                  onChange={handleCoverFeesToggle}
                  disabled={selectedAmount < minimumAmount}
                />
                <span className="checkmark"></span>
                <span className="cover-fees-text">
                  {t.coverFees}{" "}
                  <strong style={{ color: "#08337c" }}>AAAS</strong>.
                </span>
              </label>
              {coverFees && selectedAmount >= minimumAmount && (
                <div className="fees-breakdown">
                  <p>
                    {t.originalDonation} <strong>{selectedAmount.toFixed(2)} €</strong>
                  </p>
                  <p>
                    {t.processingFees} <strong>{(selectedAmount * 0.05).toFixed(2)} €</strong>
                  </p>
                  <p style={{ color: "#08337c", fontWeight: "600" }}>
                    {t.totalAmount} <strong>{calculateTotalAmount().toFixed(2)} €</strong>
                  </p>
                </div>
              )}
            </div>

    <div className="payment-form-section">
              {paymentMethod === 'checkout' && (
                <StripeCheckoutButton
                  selectedAmount={calculateTotalAmount()}
                  isMonthly={isMonthly}
                  lang={lang}
                  customerData={{
                    firstName: donationType === 'individual' ? step2Data.firstName : organizationData.companyName,
                    lastName: donationType === 'individual' ? step2Data.lastName : '',
                    email: step2Data.email,
                    phone: step2Data.phone,
                    streetAddress: step3Data.streetAddress,
                    addressLine2: step3Data.addressLine2,
                    city: step3Data.city,
                    zipCode: step3Data.zipCode,
                    country: step3Data.country,
                    state: step3Data.state,
                    donationType: donationType,
                    donationPurpose: donationPurpose,
                    dedication: dedication,
                    organizationData: organizationData,
                    membershipType: membershipType,
                    memberNumber: memberNumber,
                    lang: lang
                  }}
                  onPaymentAttempt={handlePaymentSubmit}
                />
              )}
              
              {paymentMethod === 'card' && (
                <SimplePaymentForm
                  selectedAmount={calculateTotalAmount()}
                  isMonthly={isMonthly}
                  lang={lang}
                  customerData={{
                    firstName: donationType === 'individual' ? step2Data.firstName : organizationData.companyName,
                    lastName: donationType === 'individual' ? step2Data.lastName : '',
                    email: step2Data.email,
                    phone: step2Data.phone,
                    streetAddress: step3Data.streetAddress,
                    addressLine2: step3Data.addressLine2,
                    city: step3Data.city,
                    zipCode: step3Data.zipCode,
                    country: step3Data.country,
                    state: step3Data.state,
                    donationType: donationType,
                    donationPurpose: donationPurpose,
                    dedication: dedication,
                    organizationData: organizationData,
                    membershipType: membershipType,
                    memberNumber: memberNumber,
                    lang: lang
                  }}
                  onBack={() => alert('Going back to previous step...')}
                  onClose={() => showPopupWithScrollBlock('leaving')}
                  onSuccess={(result) => {
                    console.log('Payment successful!', result);
                    alert('Thank you for your donation!');
                  }}
                  onPaymentAttempt={handlePaymentSubmit} 
                />
              )}
            </div>

            <div className="questions-container">
              <div className={`question-wrapper ${activeQuestion === "secure" ? "active" : ""}`}>
                <button 
                  className="question" 
                  onClick={() => toggleQuestion("secure")}
                  aria-expanded={activeQuestion === "secure"}
                >
                  {t.questionSecure}
                </button>
                <div className={`accordion-content ${activeQuestion === "secure" ? "open" : ""}`}>
                  <div className="accordion-content-inner">
                    <p>{t.answerSecure}</p>
                  </div>
                </div>
              </div>

              <div className={`question-wrapper ${activeQuestion === "taxDeductible" ? "active" : ""}`}>
                <button 
                  className="question" 
                  onClick={() => toggleQuestion("taxDeductible")}
                  aria-expanded={activeQuestion === "taxDeductible"}
                >
                  {t.questionTaxDeductible}
                </button>
                <div className={`accordion-content ${activeQuestion === "taxDeductible" ? "open" : ""}`}>
                  <div className="accordion-content-inner">
                    <div dangerouslySetInnerHTML={{ __html: t.answerTaxDeductible }} />
                    <div dangerouslySetInnerHTML={{ __html: t.answerTaxReceipt }} />
                  </div>
                </div>
              </div>

              <div className={`question-wrapper ${activeQuestion === "dataProcessing" ? "active" : ""}`}>
                <button 
                  className="question" 
                  onClick={() => toggleQuestion("dataProcessing")}
                  aria-expanded={activeQuestion === "dataProcessing"}
                >
                  {t.questionDataProcessing}
                </button>
                <div className={`accordion-content ${activeQuestion === "dataProcessing" ? "open" : ""}`}>
                  <div className="accordion-content-inner">
                    <p>{t.answerDataProcessing}</p>
                  </div>
                </div>
              </div>

              <div className={`question-wrapper ${activeQuestion === "cancelRecurring" ? "active" : ""}`}>
                <button 
                  className="question" 
                  onClick={() => toggleQuestion("cancelRecurring")}
                  aria-expanded={activeQuestion === "cancelRecurring"}
                >
                  {t.questionCancelRecurring}
                </button>
                <div className={`accordion-content ${activeQuestion === "cancelRecurring" ? "open" : ""}`}>
                  <div className="accordion-content-inner">
                    <p>
                      {t.answerCancelRecurring}{' '}
                      <a href="mailto:dons@aaas.fr" style={{ color: '#08337c', fontWeight: '600' }}>
                        {t.answerCancelRecurringEmail}
                      </a>.{' '}
                      {t.answerCancelRecurringEnd}
                    </p>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>

        <footer className="donation-footer" style={{
          backgroundColor: '#f8f9fa',
          padding: '40px 20px',
          borderTop: '1px solid #e0e0e0',
          marginTop: '60px',
          lineHeight: '1.4'
        }}>
          <div className="footer-container" style={{
            maxWidth: '800px',
            margin: '0 auto',
            textAlign: 'center'
          }}>
            
            <div className="footer-logos-section" style={{ marginBottom: '30px' }}>
              <a 
                href="https://aaas.fr"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src={require("../Images/logo_blue-AAAS.png")}
                  alt="AAAS Logo"
                  loading="lazy" 
                  decoding="async"
                  width="180"
                  height="40"
                  style={{ maxWidth: '180px', height: 'auto' }}
                />
              </a>
            </div>

            <div style={{
              backgroundColor: 'white',
              padding: '25px',
              borderRadius: '12px',
              border: '1px solid #e0e0e0',
              marginBottom: '25px'
            }}>
              <h3 style={{ 
                color: '#08337c', 
                marginBottom: '15px', 
                fontSize: '18px',
                fontWeight: '600'
              }}>
                {t.companyInfo}
              </h3>
              <div style={{ 
                lineHeight: '1.6', 
                color: '#333', 
                fontSize: '15px'
              }}>
                {lang === 'fr' && (
                  <>
                    <p style={{ margin: '6px 0' }}>Fondée en 1890 à Paris</p>
                    <p style={{ margin: '6px 0' }}>Reconnue d'utilité publique en 1956</p>
                    <p style={{ margin: '6px 0' }}>Association loi 1901</p>
                  </>
                )}
                {lang === 'en' && (
                  <>
                    <p style={{ margin: '6px 0' }}>Founded in Paris in 1890</p>
                    <p style={{ margin: '6px 0' }}>Recognized as a Public utility in 1956</p>
                    <p style={{ margin: '6px 0' }}>Non-Profit Law of 1901</p>
                  </>
                )}
                {lang === 'es' && (
                  <>
                    <p style={{ margin: '6px 0' }}>Fundada en París en 1890</p>
                    <p style={{ margin: '6px 0' }}>Reconocida de utilidad pública en 1956</p>
                    <p style={{ margin: '6px 0' }}>Asociación conforme a la ley de 1901</p>
                  </>
                )}
                <p style={{ margin: '6px 0', fontWeight: '600' }}>N° SIRET: 775 675 424 00011</p>
                <p style={{ margin: '6px 0', fontWeight: '600' }}>Code APE: 8899B</p>
                <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e0e0e0' }}>
                  <p style={{ margin: '6px 0' }}>77, rue la Fayette 75009, Paris, France</p>
                  <p style={{ margin: '6px 0' }}>
                    <a href="tel:+33148780299" style={{ color: '#08337c', textDecoration: 'none', fontWeight: '500' }}>
                      +33 1 48 78 02 99
                    </a>
                  </p>
                  <p style={{ margin: '6px 0' }}>
                    <a href="mailto:dons@aaas.fr" style={{ color: '#08337c', textDecoration: 'none', fontWeight: '500' }}>
                      dons@aaas.fr
                    </a>
                  </p>
                  <p style={{ margin: '6px 0' }}>
                    <a href="https://www.aaas.fr" target="_blank" rel="noopener noreferrer" style={{ color: '#08337c', textDecoration: 'none', fontWeight: '500' }}>
                      www.aaas.fr
                    </a>
                  </p>

                  <p style={{ 
                    fontSize: '16px',
                    fontStyle: 'italic',
                    color: '#08337c',
                    fontWeight: '600',
                    margin: '15px 0 0 0'
                  }}>
                    {t.thankYou}
                  </p>
                </div>
              </div>
            </div>
            <div style={{ 
              paddingTop: '15px',
              borderTop: '1px solid #dee2e6',
              textAlign: 'center'
            }}>
              <p style={{ margin: '6px 0', fontSize: '16px', color: '#08337c', fontWeight: 'bold' }}>
                AAAS Copyright © 2026 |{' '}
                <a 
                  href="https://aaas.fr/mentions-legales"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: '#08337c',
                    textDecoration: 'none',
                    fontWeight: 'bold'
                  }}
                >
                  Mentions légales
                </a>
              </p>
            </div>
          </div>
        </footer>
      </div>

      <SuccessPopup
        show={showSuccessPopup}
        onClose={handleCloseSuccessPopup}
        donationAmount={successDonationData.amount}
        isMonthly={successDonationData.isMonthly}
        lang={lang}  
      />

    </div> 
  </Elements>
);
};

export default Donation;